/**
 * Десктопная модалка создания задачи на новом бэке — редизайн сентября 2026.
 *
 * Вёрстка и порядок блоков повторяют `TaskCreateModalDesktop`: это тот же
 * экран, и расходиться им нельзя. Отличия только в данных — UUID вместо
 * числовых id и свой submit, поэтому те же `R*`-компоненты вызываются с
 * `TId = string`.
 *
 * Тело создания принимает не всё: вложения описания, привязка к сделке и
 * «сохранить как шаблон» уходят пост-шагами (POST /files, /crm-links,
 * /save-as-template) — как чек-лист и связи. Постановщик берётся из сессии:
 * пикер на месте, но заблокирован. Напоминания у задач v2 нет вовсе — этого
 * контрола в форме нет.
 *
 * «Добавить всех» в помощниках и наблюдателях берёт отделы из
 * `/v2/departments` — на задаче тот же приём делает `/assignees/department`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { useDepartmentsWithMembersV2 } from '@/entities/department/api/departments-v2';
import { CrmDealPickerV2 } from '@/entities/task/ui/CrmDealPickerV2';
import { useVoiceRecorder } from '@/pages/task-redesign/model/useVoiceRecorder';
import { TaskV2Picker } from '@/entities/task/ui/TaskV2Picker';

import { toggleId } from '../utils';
import {
  ACCEPT_BY_KIND,
  RAttachMenu,
  RBindingField,
  RChecklist,
  RClip,
  RClose,
  RDocChip,
  RMediaTile,
  RVoicePanel,
  RVoicePill,
  formatVoiceLength,
  isMediaName,
  isVideoName,
  isVoiceName,
  RDateTimeField,
  RFlagCheckbox,
  RHoursField,
  RPrioritySelect,
  RRepeat,
  RRolePicker,
  hoursToHHMM,
  totalChecklistHours,
} from '../desktop/redesign';
import { TaskV2TemplateDropdown } from './TaskV2TemplateDropdown';
import {
  NOT_PORTED_HINT,
  type TaskV2CreateFormApi,
} from './useTaskV2CreateForm';

interface Props {
  form: TaskV2CreateFormApi;
  onRequestClose: () => void;
}

const FIELD_BASE =
  'w-full rounded-[10px] bg-[#F6F7FA] px-[14px] py-[13px] text-[12px] text-[#575757] outline-none placeholder:text-[rgba(20,30,49,0.42)]';

export function TaskV2CreateModalDesktop({ form, onRequestClose }: Props) {
  const isLoading = form.isSubmitting;
  const canSubmit =
    Boolean(form.title.trim() && form.responsibleId) && !isLoading;

  const departments = useDepartmentsWithMembersV2();
  const [bindOpen, setBindOpen] = useState(false);
  const [descMenuOpen, setDescMenuOpen] = useState(false);
  const attachBtnRef = useRef<HTMLButtonElement>(null);
  const [descRecOpen, setDescRecOpen] = useState(false);
  const [voiceLengths, setVoiceLengths] = useState<Record<string, number>>({});

  const descRecorder = useVoiceRecorder({
    onSend: async (blob, filename, durationMs) => {
      const file = new File([blob], filename, { type: blob.type });
      form.setAttachedFiles((prev) => [...prev, file]);
      setVoiceLengths((prev) => ({ ...prev, [filename]: durationMs }));
      setDescRecOpen(false);
    },
  });

  // Превью картинок: object-URL живёт ровно столько, сколько файл в списке.
  const previews = useMemo(() => {
    const map = new Map<File, string>();
    for (const f of form.attachedFiles) {
      if (isMediaName(f.name) && !isVideoName(f.name)) {
        map.set(f, URL.createObjectURL(f));
      }
      if (isVoiceName(f.name)) {
        map.set(f, URL.createObjectURL(f));
      }
    }
    return map;
  }, [form.attachedFiles]);

  useEffect(() => {
    return () => {
      for (const url of previews.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, [previews]);

  const removeFileAt = (idx: number) =>
    form.setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));

  const attached = form.attachedFiles.map((file, idx) => ({ file, idx }));
  const descMedia = attached.filter((a) => isMediaName(a.file.name));
  const descVoices = attached.filter((a) => isVoiceName(a.file.name));
  const descDocs = attached.filter(
    (a) => !isMediaName(a.file.name) && !isVoiceName(a.file.name)
  );

  /* Время: сумма часов чек-листа перебивает ручной ввод — как на битриксовой
   * форме, синхронизация односторонняя. */
  const checklistHoursTotal = totalChecklistHours(form.checklist);
  const hoursFromChecklists = checklistHoursTotal > 0;
  const checklistHHMM = hoursToHHMM(checklistHoursTotal);

  useEffect(() => {
    if (!hoursFromChecklists) {
      return;
    }
    if (checklistHHMM !== form.executionTimeHHMM) {
      form.setExecutionTimeHHMM(checklistHHMM);
    }
  }, [hoursFromChecklists, checklistHHMM]);

  const uniq = (ids: string[]) => Array.from(new Set(ids));
  const creatorId = form.creator?.id ?? null;

  return (
    <div
      className={clsx(
        'my-auto w-full max-w-[660px] rounded-[22px] bg-white px-[24px] pb-[26px] pt-[22px]',
        'font-sans shadow-[0_20px_60px_rgba(20,30,49,0.12)]',
        'animate-in fade-in-0 zoom-in-95 duration-300 ease-out'
      )}
      role="dialog"
      aria-modal="true"
      aria-label="Создание задачи"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Шапка: подпись и выбор шаблона */}
      <div className="flex flex-wrap items-center gap-[12px]">
        <div className="min-w-0 flex-1 text-[12px] font-bold text-[#141E31]">
          Создайте задачу или выберите{' '}
          <span className="hidden min-[620px]:inline">шаблон</span>
        </div>
        <TaskV2TemplateDropdown onApply={form.applyTemplate} />
        <button
          type="button"
          onClick={onRequestClose}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[8px] transition hover:bg-[rgba(59,77,127,0.07)]"
          aria-label="Закрыть"
          title="Закрыть"
        >
          <RClose size={16} />
        </button>
      </div>

      {/* Привязка к сделке и приоритет. Сама привязка уходит пост-шагом —
          POST /v2/tasks/{id}/crm-links, у создания своего поля для неё нет. */}
      <div className="mt-[16px] flex flex-wrap items-center gap-x-[18px] gap-y-[14px]">
        <RBindingField
          label={
            form.crmDeal?.title ||
            (form.crmDeal?.id ? `Сделка #${form.crmDeal.id}` : 'Без привязки')
          }
          open={bindOpen}
          onToggle={() => setBindOpen((v) => !v)}
          disabled={isLoading}
        >
          <CrmDealPickerV2
            selectedId={form.crmDeal?.id ? String(form.crmDeal.id) : null}
            onPick={(deal) => {
              form.setCrmDeal(deal);
              setBindOpen(false);
            }}
            onClear={() => {
              form.setCrmDeal(null);
              setBindOpen(false);
            }}
          />
        </RBindingField>

        <RPrioritySelect
          value={form.priority}
          onChange={form.setPriority}
          disabled={isLoading}
        />
      </div>

      {/* Название */}
      <input
        value={form.title}
        disabled={isLoading}
        onChange={(e) => form.setTitle(e.target.value)}
        placeholder="Название задачи (Обязательное поле)"
        className={clsx(
          FIELD_BASE,
          'mt-[16px] border',
          form.title.trim()
            ? 'border-[rgba(59,77,127,0.14)]'
            : 'border-[rgba(59,77,127,0.10)]',
          isLoading && 'cursor-not-allowed opacity-50'
        )}
      />
      {form.titleError ? (
        <div className="mt-[6px] text-[11px] text-[#EF4642]">
          {form.titleError}
        </div>
      ) : null}

      {/* Описание с вложениями. В теле создания файлов нет — они уходят
          пост-шагом в POST /v2/tasks/{id}/files. */}
      <div className="relative mt-[10px]">
        <textarea
          value={form.description}
          disabled={isLoading}
          rows={3}
          onChange={(e) => form.setDescription(e.target.value)}
          placeholder="Описание задачи. Можете подкрепить файлы или скриншоты"
          className={clsx(
            FIELD_BASE,
            'resize-y border pr-[40px] leading-[1.5]',
            form.description.trim()
              ? 'border-[rgba(59,77,127,0.14)]'
              : 'border-[rgba(59,77,127,0.10)]',
            isLoading && 'cursor-not-allowed opacity-50'
          )}
        />

        <input
          ref={form.fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            if (picked.length) {
              form.setAttachedFiles((prev) => [...prev, ...picked]);
            }
            e.currentTarget.value = '';
          }}
        />

        <button
          ref={attachBtnRef}
          type="button"
          disabled={isLoading}
          onClick={() => setDescMenuOpen((v) => !v)}
          className={clsx(
            'absolute right-[10px] top-[10px] flex h-[26px] w-[26px] items-center justify-center rounded-[8px]',
            'transition hover:bg-[rgba(59,77,127,0.07)]',
            isLoading && 'cursor-not-allowed opacity-50'
          )}
          aria-label="Прикрепить"
          title="Прикрепить"
        >
          <RClip />
        </button>

        {descMenuOpen ? (
          <RAttachMenu
            anchorRef={attachBtnRef}
            align="end"
            sideOffset={6}
            alignOffset={-2}
            onPick={(kind) => {
              setDescMenuOpen(false);
              const input = form.fileInputRef.current;
              if (!input) {
                return;
              }
              input.accept = ACCEPT_BY_KIND[kind];
              input.click();
            }}
            onVoice={() => {
              setDescMenuOpen(false);
              setDescRecOpen(true);
              void descRecorder.start();
            }}
          />
        ) : null}

        {descRecOpen ? (
          <RVoicePanel
            anchorRef={attachBtnRef}
            align="end"
            sideOffset={6}
            alignOffset={-2}
            clipId="recclip-desc-v2"
            elapsedMs={descRecorder.elapsedMs}
            busy={descRecorder.state === 'sending'}
            onCancel={() => {
              descRecorder.cancel();
              setDescRecOpen(false);
            }}
            onSave={descRecorder.stopAndSend}
          />
        ) : null}
      </div>

      {descMedia.length > 0 ? (
        <div className="mt-[10px] flex flex-wrap items-start gap-[8px]">
          {descMedia.map(({ file, idx }) => (
            <RMediaTile
              key={`${file.name}_${idx}`}
              name={file.name}
              video={isVideoName(file.name)}
              previewUrl={previews.get(file) ?? null}
              onRemove={() => removeFileAt(idx)}
            />
          ))}
        </div>
      ) : null}

      {descVoices.length > 0 ? (
        <div className="mt-[10px] flex flex-col gap-[6px]">
          {descVoices.map(({ file, idx }) => (
            <RVoicePill
              key={`${file.name}_${idx}`}
              length={formatVoiceLength(voiceLengths[file.name] ?? 0)}
              src={previews.get(file) ?? null}
              onRemove={() => removeFileAt(idx)}
            />
          ))}
        </div>
      ) : null}

      {descDocs.length > 0 ? (
        <div className="mt-[10px] flex flex-wrap items-center gap-[6px]">
          {descDocs.map(({ file, idx }) => (
            <RDocChip
              key={`${file.name}_${idx}`}
              name={file.name}
              onRemove={() => removeFileAt(idx)}
            />
          ))}
        </div>
      ) : null}

      {/* Роли участников */}
      <div className="mt-[26px] grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-x-[28px] gap-y-[24px]">
        <RRolePicker<string>
          label="Исполнитель"
          items={form.people}
          selectedIds={form.responsibleId ? [form.responsibleId] : []}
          onPick={(id) => form.setResponsibleId(id)}
          disabled={isLoading || form.isLoadingUsers}
        />

        {/* Постановщик в v2 берётся из сессии — показываем, но не меняем. */}
        <div title={NOT_PORTED_HINT} className="cursor-not-allowed">
          <RRolePicker<string>
            label="Постановщик"
            items={form.people}
            selectedIds={creatorId ? [creatorId] : []}
            onPick={() => undefined}
            disabled
          />
        </div>

        <RRolePicker<string>
          label="Помощники"
          multiple
          items={form.people}
          selectedIds={form.accompliceIds}
          onPick={(id) =>
            form.setAccompliceIds(toggleId(form.accompliceIds, id))
          }
          onRemove={(id) =>
            form.setAccompliceIds(form.accompliceIds.filter((x) => x !== id))
          }
          departments={departments}
          onAddMany={(ids) =>
            form.setAccompliceIds(uniq([...form.accompliceIds, ...ids]))
          }
          disabled={isLoading || form.isLoadingUsers}
        />

        <RRolePicker<string>
          label="Наблюдатели"
          multiple
          items={form.people}
          selectedIds={form.auditorIds}
          onPick={(id) => form.setAuditorIds(toggleId(form.auditorIds, id))}
          onRemove={(id) =>
            form.setAuditorIds(form.auditorIds.filter((x) => x !== id))
          }
          departments={departments}
          onAddMany={(ids) =>
            form.setAuditorIds(uniq([...form.auditorIds, ...ids]))
          }
          disabled={isLoading || form.isLoadingUsers}
        />
      </div>

      {/* Сроки, напоминание и оценка. Напоминание отрабатывает на бэке:
          ReminderWorker раз в 30 секунд ищет созревшие remindAt и шлёт
          уведомление ответственному — таймеров на фронте не нужно. */}
      <div className="mt-[28px] grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-x-[28px] gap-y-[20px]">
        <span className="flex min-w-0 items-center gap-[10px]">
          <span className="flex-1 whitespace-nowrap text-[11px] text-[rgba(20,30,49,0.7)] min-[620px]:text-[12px]">
            Крайний срок
          </span>
          <RDateTimeField
            title="Крайний срок"
            value={form.deadline}
            initToNow={!form.hasInitialDeadline}
            onChange={form.setDeadline}
            disabled={isLoading}
          />
        </span>

        <span className="flex min-w-0 items-center gap-[10px]">
          <span className="flex-1 whitespace-nowrap text-[11px] text-[rgba(20,30,49,0.7)] min-[620px]:text-[12px]">
            Напомнить
          </span>
          <RDateTimeField
            title="Напоминание"
            value={form.remindAt}
            initToNow={false}
            onChange={form.setRemindAt}
            disabled={isLoading}
          />
        </span>

        <RHoursField
          value={form.executionTimeHHMM}
          onChange={form.setExecutionTimeHHMM}
          readOnly={hoursFromChecklists}
          disabled={isLoading}
        />
      </div>

      {/* Чек-листы */}
      <div className="mt-[26px]">
        {/* Соисполнители и время пункта уходят вместе с ним: у контракта
            чек-листа есть и `members`, и `planMinutes`. */}
        <RChecklist
          value={form.checklist}
          onChange={form.setChecklist}
          people={form.checklistPeople}
          disabled={isLoading}
        />
      </div>

      {/* Связь с другой задачей */}
      <div className="mt-[26px] flex items-center gap-[10px]">
        <span className="text-[12px] text-[rgba(20,30,49,0.7)]">
          Связать с другой задачей?
        </span>
        <TaskV2Picker
          selected={form.linkedTask}
          onSelect={form.setLinkedTask}
          disabled={isLoading}
          placeholder="Выбор задачи"
        />
      </div>

      {/* Флаги */}
      <div
        className={clsx(
          'mt-[26px] grid gap-x-[28px] gap-y-[16px] border-t border-[rgba(59,77,127,0.10)] pt-[22px]',
          'grid-cols-2 min-[620px]:grid-cols-[repeat(auto-fit,minmax(200px,auto))]'
        )}
      >
        <RFlagCheckbox
          checked={form.taskControl}
          onChange={form.setTaskControl}
          label="Проконтролировать задачу после завершения"
          disabled={isLoading}
        />
        <RFlagCheckbox
          checked={form.saveAsTemplate}
          onChange={form.setSaveAsTemplate}
          label="Сохранить как шаблон"
          disabled={isLoading}
        />
        <RFlagCheckbox
          checked={form.recurrenceOpen}
          onChange={form.setRecurrenceOpen}
          label="Сделать задачу регулярной"
          disabled={isLoading}
        />
      </div>

      <RRepeat
        isOpen={form.recurrenceOpen}
        disabled={isLoading}
        period={form.recurrencePeriod}
        setPeriod={form.setRecurrencePeriod}
        firstAt={form.recurrenceFirstAt}
        setFirstAt={form.setRecurrenceFirstAt}
        everyCount={form.recurrenceEvery}
        setEveryCount={form.setRecurrenceEvery}
        untilAt={form.recurrenceUntil}
        setUntilAt={form.setRecurrenceUntil}
      />

      <div className="mt-[30px] flex justify-center">
        <button
          type="submit"
          disabled={!canSubmit}
          className={clsx(
            'min-w-[168px] rounded-full bg-[#57B792] px-[20px] py-[10px]',
            'text-[12px] font-bold text-white transition',
            canSubmit
              ? 'shadow-[0_6px_16px_rgba(87,183,146,0.32)] hover:bg-[#4aa985]'
              : 'cursor-not-allowed opacity-60'
          )}
        >
          {isLoading ? 'Отправка...' : 'Создать задачу'}
        </button>
      </div>
    </div>
  );
}
